/**
 * Token resolution.
 *
 * Responsive values become CSS custom properties on the template root plus a
 * few media queries, so nodes can carry `var(--sp-sectionY)` in an inline style
 * and still respond to breakpoints. Colours are validated on import, so by the
 * time they reach here they are plain hex/rgb strings.
 */

import type { CSSProperties } from "react";
import type {
  ButtonToken,
  DividerToken,
  GradientToken,
  MotionToken,
  ResponsiveNumber,
  ShadowToken,
  TemplateTokens,
  TextStyleToken,
} from "./types";

export const BREAKPOINTS = { sm: 640, md: 768, lg: 1024 } as const;
type Breakpoint = keyof typeof BREAKPOINTS;

const MAX_WIDTHS: Record<string, string> = {
  xs: "20rem", sm: "24rem", md: "28rem", lg: "32rem", xl: "36rem",
  "2xl": "42rem", "3xl": "48rem", "4xl": "56rem", "5xl": "64rem", "6xl": "72rem",
  full: "100%", none: "none", screen: "100vw",
};

const FONT_SCALES: Record<string, number> = { small: 0.9, medium: 1, large: 1.15 };

export const fontScaleFor = (value: string | null | undefined): number =>
  FONT_SCALES[value ?? "medium"] ?? 1;

const responsive = (value: ResponsiveNumber): { base: number } & Partial<Record<Breakpoint, number>> =>
  typeof value === "number" ? { base: value } : value;

/* ------------------------------------------------------- colour overrides */

/** Apply the owner's colour picker, exactly where the host contract says it lands. */
export function applyColorScheme(tokens: TemplateTokens, hex: string | null | undefined): TemplateTokens {
  if (!hex) return tokens;

  const extended = { ...(tokens.extended ?? {}) };
  for (const [name, token] of Object.entries(extended)) {
    if (typeof token === "object" && token.overriddenBy) {
      const value =
        token.overriddenBy === "colorScheme.soft" ? `${hex}33`
        : token.overriddenBy === "colorScheme.line" ? `${hex}33`
        : hex;
      extended[name] = { ...token, value };
    }
  }

  return {
    ...tokens,
    extended,
    palette: { ...tokens.palette, accent: hex, accentSoft: `${hex}33`, line: `${hex}33` },
    surfaces: { ...tokens.surfaces, chipBackground: `${hex}20`, cardBorder: `${hex}40` },
  };
}

/* -------------------------------------------------------------- resolver */

export type TokenResolver = {
  css: string;
  rootClass: string;
  color(ref: string | undefined): string | undefined;
  background(ref: string | undefined): string | undefined;
  shadow(ref: string | undefined): string | undefined;
  border(ref: string | undefined): string | undefined;
  radius(ref: string | undefined): string | undefined;
  space(ref: string | undefined): string | undefined;
  padding(ref: string | undefined): string | undefined;
  maxWidth(ref: string | undefined): string | undefined;
  textStyle(name: string | undefined): CSSProperties;
  button(name: string | undefined): ButtonToken | undefined;
  divider(name: string): DividerToken | undefined;
  motion(name: string | undefined): MotionToken | undefined;
  fontFamily(kind: "heading" | "body" | "accent"): string;
};

const gradientCss = (gradient: GradientToken): string => {
  const stops = gradient.stops
    .map((stop) => (stop.at === undefined ? stop.color : `${stop.color} ${stop.at}%`))
    .join(", ");
  return gradient.kind === "radial"
    ? `radial-gradient(circle, ${stops})`
    : `linear-gradient(${gradient.angle ?? 180}deg, ${stops})`;
};

const shadowCss = (shadow: ShadowToken): string =>
  `${shadow.inset ? "inset " : ""}${shadow.x ?? 0}px ${shadow.y}px ${shadow.blur}px ${shadow.spread ?? 0}px ${shadow.color}`;

let rootCounter = 0;

export function createTokenResolver(
  tokens: TemplateTokens,
  options: {
    fontScale?: number;
    prefersReducedMotion?: boolean;
    /** Declared fonts, so a family resolves to "Lora, Georgia, serif" and not a bare "Lora". */
    fonts?: Array<{ family: string; fallback?: string }>;
  } = {},
): TokenResolver {
  const fontScale = options.fontScale ?? 1;
  const fallbacks = new Map((options.fonts ?? []).map((font) => [font.family, font.fallback]));
  const familyStack = (family: string): string => {
    const fallback = fallbacks.get(family);
    const quoted = /[^A-Za-z0-9-]/.test(family) ? `'${family}'` : family;
    return fallback ? `${quoted}, ${fallback}` : quoted;
  };
  rootCounter += 1;
  const rootClass = `jtpk-r${rootCounter}`;

  const extendedValue = (name: string): string | undefined => {
    const token = tokens.extended?.[name];
    if (token === undefined) return undefined;
    return typeof token === "string" ? token : token.value;
  };

  /* --- CSS custom properties, base plus one block per breakpoint --------- */

  const buckets: Record<"base" | Breakpoint, string[]> = { base: [], sm: [], md: [], lg: [] };

  const declare = (name: string, value: ResponsiveNumber, unit = "rem") => {
    const sizes = responsive(value);
    buckets.base.push(`${name}:${sizes.base}${unit}`);
    for (const bp of ["sm", "md", "lg"] as Breakpoint[]) {
      if (sizes[bp] !== undefined) buckets[bp].push(`${name}:${sizes[bp]}${unit}`);
    }
  };

  for (const [name, value] of Object.entries(tokens.spacing)) declare(`--sp-${name}`, value);
  for (const [name, style] of Object.entries(tokens.typography.styles)) {
    const sizes = responsive(style.size);
    buckets.base.push(`--fs-${name}:${(sizes.base * fontScale).toFixed(4)}rem`);
    for (const bp of ["sm", "md", "lg"] as Breakpoint[]) {
      if (sizes[bp] !== undefined) buckets[bp].push(`--fs-${name}:${(sizes[bp]! * fontScale).toFixed(4)}rem`);
    }
    // Leading and tracking are per-breakpoint too: a design system pairs a
    // specific line height with each size step, so the ratio moves with the size.
    if (style.lineHeight !== undefined) {
      const leading = responsive(style.lineHeight);
      const rem = (bp: "base" | Breakpoint) => {
        const size = sizes[bp] ?? sizes.base;
        const value = leading[bp] ?? leading.base;
        return Number((size * value * fontScale).toFixed(3));
      };
      buckets.base.push(`--lh-${name}:${rem("base")}rem`);
      for (const bp of ["sm", "md", "lg"] as Breakpoint[]) {
        if (sizes[bp] !== undefined || leading[bp] !== undefined) buckets[bp].push(`--lh-${name}:${rem(bp)}rem`);
      }
    }
    if (style.letterSpacing !== undefined) declare(`--ls-${name}`, style.letterSpacing, "em");
  }
  for (const [name, button] of Object.entries(tokens.buttons ?? {})) {
    if (button.paddingX !== undefined) declare(`--btnx-${name}`, button.paddingX);
    if (button.paddingY !== undefined) declare(`--btny-${name}`, button.paddingY);
  }

  const blocks = [`.${rootClass}{${buckets.base.join(";")}}`];
  for (const bp of ["sm", "md", "lg"] as Breakpoint[]) {
    if (buckets[bp].length) {
      blocks.push(`@media (min-width:${BREAKPOINTS[bp]}px){.${rootClass}{${buckets[bp].join(";")}}}`);
    }
  }

  /* --- keyframes for declared motion ------------------------------------ */

  for (const [name, motion] of Object.entries(tokens.motion ?? {})) {
    if (motion.primitive === "float") {
      blocks.push(
        `@keyframes jtpk-${name}{0%{transform:translateY(0)}100%{transform:translateY(-${motion.distance ?? 10}px)}}`,
      );
    } else if (motion.primitive === "fade" || motion.primitive === "pulse") {
      blocks.push(`@keyframes jtpk-${name}{0%{opacity:.4}100%{opacity:1}}`);
    }
  }
  blocks.push(`@media (prefers-reduced-motion: reduce){.${rootClass} *{animation:none!important}}`);

  /* --- responsive structure ----------------------------------------------
   * Flex direction and grid column counts are per-node and responsive, which
   * an inline style cannot express. Nodes set --dir-* / --cols-* inline and
   * these two rules read them at each breakpoint.
   */
  const cols = (level: string): string => `repeat(var(${level}), minmax(0, 1fr))`;
  blocks.push(
    `.${rootClass} .jtpk-dir{flex-direction:var(--dir-base)}`,
    `.${rootClass} .jtpk-grid{grid-template-columns:${cols("--cols-base")}}`,
    `.${rootClass} .jtpk-span{grid-column:span var(--span-base,1)}`,
    `@media (min-width:${BREAKPOINTS.sm}px){` +
      `.${rootClass} .jtpk-dir{flex-direction:var(--dir-sm,var(--dir-base))}` +
      `.${rootClass} .jtpk-grid{grid-template-columns:${cols("--cols-sm,var(--cols-base)")}}` +
      `.${rootClass} .jtpk-span{grid-column:span var(--span-sm,var(--span-base,1))}}`,
    `@media (min-width:${BREAKPOINTS.md}px){` +
      `.${rootClass} .jtpk-dir{flex-direction:var(--dir-md,var(--dir-sm,var(--dir-base)))}` +
      `.${rootClass} .jtpk-grid{grid-template-columns:${cols("--cols-md,var(--cols-sm,var(--cols-base))")}}` +
      `.${rootClass} .jtpk-span{grid-column:span var(--span-md,var(--span-sm,var(--span-base,1)))}}`,
    `@media (min-width:${BREAKPOINTS.lg}px){` +
      `.${rootClass} .jtpk-dir{flex-direction:var(--dir-lg,var(--dir-md,var(--dir-sm,var(--dir-base))))}` +
      `.${rootClass} .jtpk-grid{grid-template-columns:${cols("--cols-lg,var(--cols-md,var(--cols-sm,var(--cols-base)))")}}` +
      `.${rootClass} .jtpk-span{grid-column:span var(--span-lg,var(--span-md,var(--span-sm,var(--span-base,1))))}}`,
  );

  /* --- accessors --------------------------------------------------------- */

  const color = (ref: string | undefined): string | undefined => {
    if (!ref) return undefined;
    const [group, name] = ref.split(".");
    if (group === "palette") return tokens.palette[name];
    if (group === "extended") return extendedValue(name);
    if (group === "gradient") {
      const gradient = tokens.gradients?.[name];
      return gradient ? gradientCss(gradient) : undefined;
    }
    // surfaces tokens point at another token; follow one hop.
    if (group === "surface" || group === "surfaces") return color(tokens.surfaces[name]);
    return undefined;
  };

  const background = (ref: string | undefined): string | undefined => {
    if (!ref) return undefined;
    const [group, name] = ref.split(".");
    if (group === "gradient") {
      const gradient = tokens.gradients?.[name];
      return gradient ? gradientCss(gradient) : undefined;
    }
    if (group === "surfaces" || group === "surface") {
      const target = tokens.surfaces[name];
      return target ? background(target) ?? color(target) : undefined;
    }
    return color(ref);
  };

  const space = (ref: string | undefined): string | undefined =>
    ref && ref in tokens.spacing ? `var(--sp-${ref})` : undefined;

  return {
    css: blocks.join("\n"),
    rootClass,
    color,
    background,

    shadow(ref) {
      if (!ref) return undefined;
      const name = ref.startsWith("shadow.") ? ref.slice(7) : ref;
      const token = tokens.shadows?.[name];
      return token ? shadowCss(token) : undefined;
    },

    border(ref) {
      if (!ref) return undefined;
      const token = tokens.borders?.[ref];
      if (!token) return undefined;
      return `${token.width}px ${token.style ?? "solid"} ${color(token.color) ?? token.color}`;
    },

    radius(ref) {
      if (!ref) return undefined;
      const value = tokens.radii[ref];
      return value === undefined ? undefined : `${value}px`;
    },

    space,

    padding(ref) {
      if (!ref) return undefined;
      const parts = ref.split(" ").map((part) => space(part) ?? "0");
      return parts.length === 1 ? parts[0] : parts.join(" ");
    },

    maxWidth(ref) {
      if (!ref) return undefined;
      return MAX_WIDTHS[ref] ?? undefined;
    },

    textStyle(name) {
      if (!name) return {};
      const style: TextStyleToken | undefined = tokens.typography.styles[name];
      if (!style) return {};
      const family = tokens.typography.families[style.family] ?? tokens.typography.families.body;
      const css: CSSProperties = {
        fontFamily: familyStack(family),
        fontSize: `var(--fs-${name})`,
      };
      if (style.weight) css.fontWeight = style.weight;
      if (style.lineHeight !== undefined) css.lineHeight = `var(--lh-${name})`;
      if (style.letterSpacing !== undefined) css.letterSpacing = `var(--ls-${name})`;
      if (style.transform && style.transform !== "none") css.textTransform = style.transform;
      if (style.italic) css.fontStyle = "italic";
      if (style.tabularNums) css.fontVariantNumeric = "tabular-nums";
      return css;
    },

    button(name) {
      return name ? tokens.buttons?.[name] : undefined;
    },

    divider(name) {
      return tokens.dividers?.[name];
    },

    motion(name) {
      return name ? tokens.motion?.[name] : undefined;
    },

    fontFamily(kind) {
      return familyStack(tokens.typography.families[kind] ?? tokens.typography.families.body);
    },
  };
}
